// frontend/src/hooks/useQueueWidget.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClinicInfo } from '../services/api';
import { useLanguage } from './useLanguage';

/**
 * Виджет электронной очереди (Earlyone) в окне поверх сайта.
 * Ссылка — из настроек клиники; пусто → available=false, кнопки не показываем.
 * `modal` нужно отрисовать рядом с местом, откуда зовётся `open`.
 */
export const useQueueWidget = () => {
  const { data: clinicInfo } = useClinicInfo();
  const { t } = useLanguage();
  const url = clinicInfo?.[0]?.queue_widget_url || '';
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  // Портал в body: шапка сайта (sticky, z-50) иначе перекрыла бы окно
  const modal = isOpen && url
    ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-0 sm:p-4"
          onMouseDown={e => { if (e.target === e.currentTarget) close(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('queueWidget.title')}
            className="relative flex h-full w-full max-w-[760px] flex-col overflow-hidden bg-[#fdfdfd] dark:bg-[#1f1f1f] sm:h-[90vh] sm:rounded-3xl"
          >
            <div className="flex items-center justify-between px-4 py-3">
              <h2 className="text-base font-bold text-[#454545] dark:text-[#e4e4e4]">
                {t('queueWidget.title')}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label={t('queueWidget.close')}
                className="flex h-9 w-9 items-center justify-center rounded-full text-2xl leading-none text-[#454545] hover:bg-[#edeef2] dark:text-[#e4e4e4] dark:hover:bg-[#192e4d]"
              >
                ×
              </button>
            </div>
            <iframe
              src={url}
              title={t('queueWidget.title')}
              className="w-full flex-1 border-0"
              allow="clipboard-write"
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  return { available: Boolean(url), open, modal };
};
